import { useId } from "react";

export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
}

const focusRing =
  "peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[color:var(--focus)]";

/** A single choice out of a few, built from native radio buttons (arrow keys work, screen readers announce it as a group). */
export function Segmented<T extends string | number>({
  legend,
  hint,
  value,
  options,
  onChange,
}: {
  legend: string;
  hint?: string;
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
}) {
  const name = useId();
  const hintId = `${name}-hint`;
  return (
    <fieldset className="min-w-0" aria-describedby={hint ? hintId : undefined}>
      <legend className="mb-1.5 text-sm font-semibold text-ink">{legend}</legend>
      <div className="inline-flex max-w-full flex-wrap gap-0.5 rounded-lg border border-line bg-surface-2 p-0.5">
        {options.map((o) => (
          <label key={String(o.value)} className="relative">
            <input
              type="radio"
              name={name}
              className="peer sr-only"
              checked={o.value === value}
              onChange={() => onChange(o.value)}
            />
            <span
              className={`grid min-h-11 min-w-11 cursor-pointer place-items-center rounded-md px-3 text-sm font-medium text-ink transition-colors hover:bg-surface peer-checked:bg-accent peer-checked:text-accent-ink ${focusRing}`}
            >
              {o.label}
            </span>
          </label>
        ))}
      </div>
      {hint && (
        <p id={hintId} className="mt-1.5 text-sm text-ink-2">
          {hint}
        </p>
      )}
    </fieldset>
  );
}
